import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Heart, Clock, Check, ChevronRight, Bell, MapPin, Eye, BookMarked } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORY_COLORS = {
  'Food & Drink': '#C8A97E', 'Nightlife': '#6B7CFF',
  'Fitness & Wellness': '#5DBB63', 'Activities': '#C07CFF', 'Free Deals': '#FF7C7C',
  'Convenience Stores': '#5B9EA0', 'Liquor Stores': '#8B4A62',
};

function CodeStatusBadge({ claim }) {
  const now = new Date().toISOString();
  if (claim.is_redeemed) return <span className="text-xs font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded-full flex items-center gap-1"><Check size={10} /> Redeemed</span>;
  if (claim.is_expired || claim.expires_at < now) return <span className="text-xs font-bold text-red-500 bg-red-100 px-2 py-0.5 rounded-full flex items-center gap-1"><Clock size={10} /> Expired</span>;
  return <span className="text-xs font-bold text-[#C8A97E] bg-[#C8A97E]/15 px-2 py-0.5 rounded-full flex items-center gap-1"><Sparkles size={10} /> Active</span>;
}

export default function StudentDashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('claims');

  useEffect(() => {
    fetch(`${API}/users/dashboard`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-[#F6F1E9]">
      <Navbar />
      <div className="flex items-center justify-center py-32">
        <div className="w-10 h-10 border-4 border-[#C8A97E] border-t-transparent rounded-full animate-spin" />
      </div>
    </div>
  );

  const claims = data?.claims || [];
  const savedDeals = data?.saved_deals || [];
  const stats = data?.stats || {};
  const activeClaims = claims.filter(c => !c.is_redeemed && !c.is_expired && c.expires_at > new Date().toISOString());

  return (
    <div className="min-h-screen bg-[#F6F1E9]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-10">

        {/* Header */}
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A8B] mb-1">Student Dashboard</p>
          <h1 className="text-3xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="student-dashboard-title">
            Hi, {user?.name?.split(' ')[0] || 'Student'} 👋
          </h1>
          <p className="text-[#2B2B2B]/55 text-sm mt-1">Your deals, codes, and activity — all in one place.</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Claims', value: stats.total_claims || 0, color: '#C8A97E', icon: Sparkles },
            { label: 'Redeemed', value: stats.redeemed || 0, color: '#5DBB63', icon: Check },
            { label: 'Active Codes', value: stats.active_codes || 0, color: '#6B7CFF', icon: Clock },
            { label: 'Saved Deals', value: stats.saved_deals || 0, color: '#C07CFF', icon: Heart },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-2xl p-4 border border-[#8A9A8B]/10 flex items-center gap-3" data-testid={`student-stat-${s.label.toLowerCase().replace(' ', '-')}`}>
              <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: s.color + '18' }}>
                <s.icon size={16} style={{ color: s.color }} />
              </div>
              <div>
                <p className="text-xs text-[#2B2B2B]/50">{s.label}</p>
                <p className="text-xl font-bold" style={{ color: s.color, fontFamily: 'Playfair Display, serif' }}>{s.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Active Codes Alert */}
        {activeClaims.length > 0 && (
          <div className="bg-[#C8A97E]/12 border border-[#C8A97E]/30 rounded-2xl p-4 mb-6 flex items-center justify-between" data-testid="active-codes-banner">
            <div className="flex items-center gap-3">
              <Sparkles size={18} className="text-[#C8A97E]" />
              <div>
                <p className="font-bold text-[#2B2B2B] text-sm">You have {activeClaims.length} active claim code{activeClaims.length > 1 ? 's' : ''}!</p>
                <p className="text-xs text-[#2B2B2B]/55">Hurry — codes expire 15 minutes after claiming.</p>
              </div>
            </div>
            <button onClick={() => setActiveTab('claims')} className="text-xs font-bold text-[#C8A97E] underline">View Codes</button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-2xl p-1.5 border border-[#8A9A8B]/10 mb-6 overflow-x-auto" data-testid="student-tabs">
          {[
            { id: 'claims', label: 'Claim History', icon: Sparkles },
            { id: 'saved', label: 'Saved Deals', icon: Heart },
            { id: 'notifications', label: 'Alerts', icon: Bell },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              data-testid={`student-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap ${activeTab === t.id ? 'bg-[#2B2B2B] text-[#F6F1E9]' : 'text-[#2B2B2B]/60 hover:text-[#2B2B2B]'}`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {/* CLAIMS TAB */}
        {activeTab === 'claims' && (
          <div data-testid="claims-panel">
            {claims.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-[#8A9A8B]/10">
                <Sparkles size={36} className="text-[#C8A97E]/40 mx-auto mb-4" />
                <h3 className="font-bold text-[#2B2B2B] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>No claims yet</h3>
                <p className="text-sm text-[#2B2B2B]/55 mb-5">Discover deals and click "Get My Claim Code" to start saving.</p>
                <Link to="/explore" className="btn-primary inline-flex">Explore Deals <ChevronRight size={14} /></Link>
              </div>
            ) : (
              <div className="space-y-3">
                {claims.map(claim => {
                  const deal = claim.deal || {};
                  const catColor = CATEGORY_COLORS[deal.category] || '#C8A97E';
                  const now = new Date().toISOString();
                  const isActive = !claim.is_redeemed && !claim.is_expired && (claim.expires_at || '') > now;
                  return (
                    <div key={claim.claim_id} className={`bg-white rounded-2xl p-4 border flex gap-4 items-start ${isActive ? 'border-[#C8A97E]/40 shadow-sm' : 'border-[#8A9A8B]/10'}`} data-testid={`claim-row-${claim.claim_id}`}>
                      {deal.image_url && (
                        <img src={deal.image_url} alt={deal.title} className="w-14 h-14 rounded-xl object-cover flex-shrink-0" onError={e => { e.target.style.display = 'none'; }} />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div>
                            <p className="text-xs font-semibold text-[#8A9A8B]">{deal.business_name || claim.business_name}</p>
                            <p className="font-bold text-[#2B2B2B] text-sm line-clamp-1" style={{ fontFamily: 'Playfair Display, serif' }}>{deal.title || claim.deal_title}</p>
                          </div>
                          <CodeStatusBadge claim={{ ...claim, is_expired: claim.is_expired || (claim.expires_at || '') < now }} />
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          {isActive ? (
                            <span className="font-mono text-base font-bold text-[#2B2B2B] tracking-widest bg-[#F6F1E9] px-3 py-1 rounded-lg" data-testid="active-claim-code">{claim.claim_code}</span>
                          ) : (
                            <span className="font-mono text-sm text-[#2B2B2B]/35 line-through tracking-widest">{claim.claim_code}</span>
                          )}
                          <span className="text-xs text-[#2B2B2B]/40">{new Date(claim.claimed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        </div>
                        {deal.city_name && (
                          <p className="text-xs text-[#8A9A8B] mt-1 flex items-center gap-1"><MapPin size={10} /> {deal.city_name}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* SAVED DEALS TAB */}
        {activeTab === 'saved' && (
          <div data-testid="saved-panel">
            {savedDeals.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-[#8A9A8B]/10">
                <Heart size={36} className="text-[#C8A97E]/40 mx-auto mb-4" />
                <h3 className="font-bold text-[#2B2B2B] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>No saved deals yet</h3>
                <p className="text-sm text-[#2B2B2B]/55 mb-5">Tap the heart icon on any deal to save it for later.</p>
                <Link to="/explore" className="btn-primary inline-flex">Explore Deals <ChevronRight size={14} /></Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {savedDeals.map(deal => (
                  <Link
                    key={deal.deal_id}
                    to={`/deals/${deal.deal_id}`}
                    className="deal-card bg-white rounded-2xl overflow-hidden border border-[#8A9A8B]/10 flex gap-3 p-3 group hover:shadow-md transition-all"
                    data-testid={`saved-deal-${deal.deal_id}`}
                  >
                    <img src={deal.image_url} alt={deal.title} className="w-16 h-16 rounded-xl object-cover flex-shrink-0" onError={e => { e.target.src = 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=200&q=60'; }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-[#8A9A8B] mb-0.5">{deal.business_name}</p>
                      <p className="font-bold text-sm text-[#2B2B2B] line-clamp-2 leading-snug" style={{ fontFamily: 'Playfair Display, serif' }}>{deal.title}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        {deal.discount_text && <span className="text-xs font-bold" style={{ color: CATEGORY_COLORS[deal.category] || '#C8A97E' }}>{deal.discount_text}</span>}
                        <span className="text-xs text-[#8A9A8B]">{deal.city_name}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* NOTIFICATIONS TAB */}
        {activeTab === 'notifications' && (
          <div data-testid="notifications-panel" className="space-y-4">
            <div className="bg-white rounded-2xl p-6 border border-[#8A9A8B]/10">
              <h3 className="font-bold text-[#2B2B2B] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Deal Alerts</h3>
              <div className="space-y-4">
                {[
                  { label: 'New deals nearby', desc: 'Get notified when a new deal is added in your city', type: 'push' },
                  { label: 'Deal ending soon', desc: 'Get reminded 24 hours before a saved deal expires', type: 'push' },
                  { label: 'Free food alerts', desc: 'Instant alerts for free deals and giveaways', type: 'push' },
                  { label: 'Daily Perks digest', desc: 'A morning roundup of the best deals near you', type: 'email' },
                ].map(item => (
                  <div key={item.label} className="flex items-center justify-between gap-4 py-3 border-b border-[#8A9A8B]/10 last:border-0">
                    <div>
                      <p className="font-semibold text-sm text-[#2B2B2B]">{item.label}</p>
                      <p className="text-xs text-[#2B2B2B]/50 mt-0.5">{item.desc}</p>
                    </div>
                    <span className="text-xs bg-[#8A9A8B]/10 text-[#8A9A8B] px-2 py-1 rounded-full capitalize flex-shrink-0">{item.type}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-[#2B2B2B]/45 mt-4">
                Click the 🔔 bell icon in the navigation bar to manage your push notification preferences.
              </p>
            </div>

            <div className="bg-[#2B2B2B] rounded-2xl p-5">
              <p className="font-bold text-white mb-1">Subscribe to Daily Perks</p>
              <p className="text-white/55 text-xs mb-4">Get the best deals in your city every morning.</p>
              <Link to="/" className="inline-flex items-center gap-2 bg-[#C8A97E] text-white text-sm font-bold px-4 py-2.5 rounded-full hover:bg-[#b8956b] transition-colors">
                <Bell size={14} /> Manage Notifications
              </Link>
            </div>
          </div>
        )}

        {/* CTA row */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Link to="/explore" className="btn-primary flex-1 justify-center py-3.5 text-sm">
            Find More Deals <ChevronRight size={14} />
          </Link>
          <Link to="/" className="btn-secondary flex-1 justify-center py-3.5 text-sm">
            Back to Home
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
